import { useAppContext } from "../../context/AppContext";
import { ProfileTrans, SuccessTrans, ToasterTrans } from "../../utils/translations";
import bgImg from "../../assets/images/bgImg-3.png";
import profilePlaceholder from "../../assets/profile.png";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { updateUser } from "../../services/userService";
import { getLinks } from "../../services/linksService";
import { trackPixelEvent } from "../../utils/pixel";
import { cancelSubscription } from "../../services/paymentService";

export default function Profile() {
  const { language, user, token, setUser, setToaster, getNextBillingDate, setLoading } = useAppContext();
  const navigate = useNavigate();
  const [showPlanOptions, setShowPlanOptions] = useState(false);
  const [username, setUsername] = useState("");
  const [isModified, setIsModified] = useState(false);
  const [links, setLinks] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const modalRef = useRef(null);


  useEffect(() => {
    setUsername(user?.username || "");
    if (!links.length) {
      (async () => {
        const fetchedLinks = await getLinks();
        setLinks(fetchedLinks);
      })();
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowConfirm(false);
      }
    };
    if (showConfirm) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showConfirm]);

  const handleCancelSubscription = async () => {
    setShowConfirm(false)
    try {
      setLoading(true);
      const res = await cancelSubscription({
        subscriptionId: user?.subscriptions[user?.subscriptions?.length - 1].
          orderId
      }, token);
      if (res.status === 200) {
        setLoading(false);
        window.location.assign("/unsubscribeSuccess")
      };

    } catch (error) {
      console.error("Error subscription canceling", error);
      setLoading(false);
      setToaster({
        message: "Ошибка при отмене подписки. Пожалуйста, попробуйте снова или напишите на anna.joy.inbox@gmail.com.",
        type: "error",
        show: true,
      });
    }
  };

  const calculateIntensity = (registerDate, practicesCompleted) => {
    if (user) {
      const today = new Date();
      const startDate = new Date(registerDate);
      const totalWeeks = Math.ceil(
        (today - startDate) / (1000 * 60 * 60 * 24 * 7)
      );
      const intensity = Math.round(totalWeeks > 0 ? (practicesCompleted / totalWeeks).toFixed(1) : 0);
      return `${intensity} ${language === "russian" ? "р/нед" : "x/week"}`;
    }
  };

  const filterLinksByLanguage = (type) => {
    return links.filter(
      (link) => link.type === type && link.language === language && link.active
    );
  };

  return (
    <main className="min-h-screen flex flex-col bg-gray-50 pb-20">
      <div
        className="w-full h-[55vh] bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${bgImg})`,
          backgroundPosition: 'right 1/2',
        }}
      ></div>

      <div className="w-10/12 sm:w-8/12 mx-auto mt-8">
        <h1 className="text-2xl font-bold text-center text-gray-800 mb-8">
          {ProfileTrans[language].header}
        </h1>


        {/* Profile Section */}
        <div className="md:flex flex-col md:flex-row items-center mb-8 bg-white p-6 rounded-lg shadow-md space-y-6 md:space-y-0">
          {/* Left Section: Profile */}
          <div className="flex items-center space-x-6 flex-1">
            <img
              src={ user?.picture || profilePlaceholder }
              alt="User Profile"
              className="w-20 h-20 rounded-full object-cover border-4 lg:block hidden"
              onError={(e) => {
                e.currentTarget.src = profilePlaceholder;
              }}
            />
            <div>
              <h5 className="lg:text-left text-center">{user?.username}</h5>
              <p>
                {ProfileTrans[language].memberSince}{" "}
                {user &&
                  new Date(user?.created_at)
                    .toISOString()
                    .slice(0, 10)
                    .split("-")
                    .reverse()
                    .join(".")}
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="h-20 border-l border-gray-300 mx-10 hidden md:block"></div>

          {/* Right Section */}
          <div className="flex flex-row md:justify-evenly flex-1 gap-10">
            <div className="text-center">
              <h5>{ProfileTrans[language].classes}</h5>
              <p>{user?.completedPractices?.length || 0}</p>
            </div>

            <div className="text-center ">
              <h5>{ProfileTrans[language].intensity}</h5>
              <p>
                {calculateIntensity(
                  user?.created_at,
                  user?.completedPractices?.length
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8">
          <div className="space-y-4 bg-white p-6 rounded-lg shadow-md">
            <h5 className="text-center">
              {ProfileTrans[language].onSightEvents}
            </h5>
            <ul className="text-gray-700 space-y-2">
              {filterLinksByLanguage("onSightEvent").length ? (
                filterLinksByLanguage("onSightEvent").map(
                  (filteredLink, index) => {
                    const eventDate = new Date(filteredLink.eventDate);
                    const formattedDate = `${eventDate
                      .getDate()
                      .toString()
                      .padStart(2, "0")}.${(eventDate.getMonth() + 1)
                        .toString()
                        .padStart(2, "0")}.${eventDate.getFullYear()}`;
                    return filteredLink.url && filteredLink.active && (
                      <li key={index} className="mb-4">
                        <a
                          href={filteredLink.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline"
                        >
                          {filteredLink.name}
                        </a>
                        <div className="text-gray-600 text-sm">
                          <span>{formattedDate}</span>{" "}
                          <span>{filteredLink.eventType}</span>
                        </div>
                      </li>
                    )
                  }
                )
              ) : (
                <p className="mt-4">{ProfileTrans[language].nothingPlanned}</p>
              )}
            </ul>
          </div>

          <div className="space-y-4 bg-white p-6 rounded-lg shadow-md">
            <h5 className="text-center">
              {ProfileTrans[language].onlineEvents}
            </h5>
            <ul className="text-gray-700 space-y-2">
              {filterLinksByLanguage("onlineEvent").length ? (
                filterLinksByLanguage("onlineEvent").map(
                  (filteredLink, index) => {
                    const eventDate = new Date(filteredLink.eventDate);
                    const formattedDate = `${eventDate
                      .getDate()
                      .toString()
                      .padStart(2, "0")}.${(eventDate.getMonth() + 1)
                        .toString()
                        .padStart(2, "0")}.${eventDate.getFullYear()}`;
                    return filteredLink.url ? (
                      <li key={index} className="mb-4">
                        <a
                          href={filteredLink.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline"
                        >
                          {filteredLink.name}
                        </a>
                        <div className="text-gray-600 text-sm">
                          <span>{formattedDate}</span>{" "}
                          <span>{filteredLink.eventType}</span>
                        </div>
                      </li>
                    ) : (
                      <p className="mt-4">
                        {ProfileTrans[language].nothingPlanned}
                      </p>
                    );
                  }
                )
              ) : (
                <p className="mt-4">{ProfileTrans[language].nothingPlanned}</p>
              )}
            </ul>
          </div>
        </div>

        {/* Plan & Classes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8">
          <div className="space-y-4 bg-white p-6 rounded-lg shadow-md">
            <h5 className="text-center">
              {ProfileTrans[language].currentPlan}: {" "}
              <span className="mt-2 text-gray-700">
                {user?.role?.charAt(0).toUpperCase() +
                  user?.role?.slice(1).toLowerCase()}
              </span>
            </h5>
            {user?.role === "premium" && (
              <div>
                {user?.subscriptions?.[user.subscriptions.length - 1]?.endDate ? (
                  <p className="text-orange-500">
                    Подписка отменена — доступ сохранится до:{" "}
                    {new Date(user.subscriptions[user.subscriptions.length - 1].endDate).toLocaleDateString("ru-RU")}
                  </p>
                ) : (
                  <>
                    <p>
                      {SuccessTrans[language].nextPayment}:{" "}
                      {getNextBillingDate(
                        user.subscriptions[user.subscriptions.length - 1].startDate
                      ).toLocaleDateString("ru-RU")}
                    </p>
                    <button
                      onClick={() => setShowConfirm(true)}
                      className="mt-4 hover:underline"
                    >
                      {ProfileTrans[language].changePlan}
                    </button>
                  </>
                )}
              </div>
            )}
            {user?.role === "basic" && (
              <button
              onClick={() => window.location.assign("/onlineClub")}
              className="mt-4 px-6 py-2 bg-pink-600 rounded-full shadow hover:bg-pink hover:text-white transition border border-orange-400"
            >
              {ProfileTrans[language].subscribe}
            </button>
            )}
          </div>
          <div className="space-y-4 bg-white p-6 rounded-lg shadow-md">
            <h5 className="text-center mb-3">{ProfileTrans[language].classes}</h5>
            <a href="/saved" onClick={() => trackPixelEvent("NavigateToSavedPractices")} className="underline text-gray-700 hover:underline pointer">
              {ProfileTrans[language].savedClasses}{" "}
              {user?.savedPractices?.length}
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8">
          <div className="space-y-4 bg-white p-6 rounded-lg shadow-md">
            <a
              href={
                links.length &&
                  user?.role === "basic" ? links.find(
                    (link) =>
                      link.type === "telegram" && link.language === language
                  )?.url : links.find(
                    (link) =>
                      link.type === "telegram_premium" && link.language === language
                  )?.url
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-pink-600 hover:underline underline"
            >
              Telegram {ProfileTrans[language].chat}
            </a>
          </div>

          <div className="space-y-4 bg-white p-6 rounded-lg shadow-md">
            <a
              href="mailto:anna.joy.inbox@gmail.com?subject=FromOUOAM&body=Hey%20there"
              className="text-pink-600 hover:underline underline"
            >
              {ProfileTrans[language].contactSupport} (Email)
            </a>
          </div>
        </div>
      </div>
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div ref={modalRef} className="bg-white p-4 rounded-lg shadow-lg max-w-sm text-center">
            <h2 className="text-lg font-semibold mb-4 text-center">Вы уверены, что хотите отменить подписку?</h2>
            <span className="text-gray-600 text-sm">Если сомневаетесь — </span>
            <a href="https://wa.me/972559116975"
              target="_blank"
              rel="noopener noreferrer" className="text-gray-600 text-sm mb-4 text-pink-600 underline">
              напишите мне в WhatsApp ❤️ <br />
            </a>
            <div className="flex justify-around mt-5">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded bg-green-300 hover:text-white text-sm"
              >
                Оставить
              </button>
              <button
                onClick={handleCancelSubscription}
                className="px-4 py-2 rounded bg-red-400 hover:text-white text-sm"
              >
                Отменить
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

